/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  Updates the URL to match the current map location and search filters
 */

import type { AutocompleteBuildingResult, AutocompleteResult } from '@zcode/bizos'
import { PropertyFeatures } from '@zcode/enums'
import {
  deepclone,
  getFittedBoundsAndZoom,
  keepBetaParam,
  localStorageSingleton,
  locationDescriptorToString,
} from '@zcode/nr-utils'
import { match, push, replace } from '@zcode/nr/es/shared/packages/history-middleware'
import { getRouteProps } from '@zcode/nr/es/shared/packages/history-middleware/actions'
import type { Dispatch } from '@zcode/nr/es/shared/types'
import { getUrlFromLocation } from '@zcode/nr/es/shared/utils/urls'
import type { GetState } from '@zcode/state-type'
import { createPath } from 'history'
import { getRelativeUrlFromFiltersAndLocation } from '../../../helpers/urls'
import { guessMapDimensions } from '../../../helpers/urls/guessMapDimensions'
import { airBnbFriendlyPathnames } from '../../../hooks/constants'
import { selectLocation } from '../../currentSearch/selectors'
import { selectToggleValue } from '../../currentSearchView/selectors'
import { selectFilters } from '../../filters/selectors'
import { selectMapDimensions, selectMapUnsynced } from '../../mainMap/selectors'

export const ZOOM_CITY_MIN = 9
export const APTS_BASE_URL = 'apartments-for-rent'
export const BLDG_BASE_URL = 'apartment-buildings'

export const updateUrlFromLocationFilter =
  ({
    shouldReplace,
    omitBox,
    keepSameBox,
    result,
  }: {
    keepSameBox?: boolean
    omitBox?: boolean
    result?: Exclude<AutocompleteResult, AutocompleteBuildingResult> | null
    shouldReplace?: boolean
  } = {}) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState()
    const { history } = dispatch(getRouteProps())
    const unsynced = selectMapUnsynced(state)

    const filters = selectFilters(state)
    const location = selectLocation(state)
    const toggleValue = selectToggleValue(state)
    const isFullWidthMap = toggleValue === 'full-map'
    const { box, lat, lng, zoom } = location ?? {}
    const search = window?.location.search
    const currentBox = new URLSearchParams(search).get('box')
    const currentBetaFeature = new URLSearchParams(search).get('beta')
    const currentViewType = new URLSearchParams(search).get('view_type')
    const reallyOmitBox = omitBox || (!currentBox && keepSameBox)
    const center = { lat, lng }
    // For state zooms we don't care to limit the zoom. ZOOM_CITY_MIN is the min zoom that still
    // shows clusters on the map.
    const isStateZoom = zoom && zoom < ZOOM_CITY_MIN
    const minZoom = isStateZoom ? undefined : ZOOM_CITY_MIN
    const dimensions = guessMapDimensions(selectMapDimensions(state), isFullWidthMap)

    localStorageSingleton.set('prevSearchFilters', filters)

    let fittedBox: ReturnType<typeof getFittedBoundsAndZoom>['box'] | undefined
    let fittedZoom = zoom
    if (!unsynced) {
      const fittedViewport = getFittedBoundsAndZoom({
        box: reallyOmitBox ? undefined : box,
        center,
        dimensions,
        minZoom,
      })
      if (reallyOmitBox) {
        fittedBox = undefined
      } else {
        fittedBox = fittedViewport.box
      }
      fittedZoom = fittedViewport.zoom
    }
    const locationUrl = getUrlFromLocation({
      ...location,
      box: fittedBox || box,
      zoom: reallyOmitBox ? zoom : fittedZoom,
    })

    // We need to remove the Airbnb-friendly filter if we're navigating from
    // an Airbnb-friendly market to a non-Airbnb-friendly market with that filter set
    let isAirbnbFriendlyPathname = false
    const filtersCopy = deepclone(filters)
    for (const airBnbFriendlyPathname of airBnbFriendlyPathnames) {
      const cityUrl = locationUrl.path.split('/')[0]
      if (airBnbFriendlyPathname.includes(cityUrl)) {
        isAirbnbFriendlyPathname = true
        break
      }
    }

    if (!isAirbnbFriendlyPathname && filters.features.includes(PropertyFeatures.AIRBNB_FRIENDLY)) {
      const index = filters.features.indexOf(PropertyFeatures.AIRBNB_FRIENDLY)
      filtersCopy.features.splice(index, 1)
    }

    const relUrl = getRelativeUrlFromFiltersAndLocation({
      currentUrl: locationUrl.path,
      filters: filtersCopy,
      locationUrlQueries: locationUrl.searchQueries,
      omitBox: reallyOmitBox,
    }).replace(/\/{2,}/g, '/')

    const hasBaseUrl = relUrl.includes(APTS_BASE_URL) || relUrl.includes(BLDG_BASE_URL)

    const relUrlWithWithBase = `/${APTS_BASE_URL}/${relUrl}`

    let url = hasBaseUrl ? relUrl : relUrlWithWithBase

    if (currentViewType) {
      const params = new URLSearchParams(url.includes('?') ? url.split('?')[1] : '')
      params.set('view_type', currentViewType)

      url = `${url.split('?')[0]}?${params.toString()}`
    }

    if (currentBetaFeature) {
      url = locationDescriptorToString(keepBetaParam(url), history)
    }

    dispatch(
      match(undefined, (routeProps) => {
        const { location } = routeProps
        const prevUrl = createPath(location)
        // create a history stack if URL is different
        if (url !== prevUrl || shouldReplace || shouldReplace) {
          const state = result ? { result } : {}
          // shouldReplace should only ever be true for updates coming from syncMapChange
          if (shouldReplace) {
            dispatch(replace(url, state))
          } else {
            dispatch(push(url, state))
          }
        }
      })
    )
  }
